import React, { useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Home, Camera, MessageCircle, LucideIcon } from "lucide-react";
import { cn } from "@/shared/lib/utils";
import { Badge } from "@/shared/ui/badge";
import { audioFeedback } from "@/shared/api/audioFeedback";
import { prefetchRoute } from "@/shared/lib/routePrefetch";
import { useSimpleMode } from "@/contexts/SimpleModeContext";
import { CurrencyIcon } from "@/components/layout/CountryCurrency";
import { useOutboxBadgeCount } from "@/features/outbox/OutboxUi";

export interface NavItem {
  id: string;
  label: string;
  icon?: LucideIcon;
  customIcon?: React.ReactNode;
  badge?: number;
}

export interface BottomNavProps {
  activeTab: string;
  onTabChange: (tabId: string) => void;
  className?: string;
}

const iconBgColors: Record<string, string> = {
  home: "bg-green-500/20",
  capture: "bg-blue-500/20",
  agent: "bg-purple-500/20",
  score: "bg-yellow-500/20",
};

export const BottomNav: React.FC<BottomNavProps> = ({
  activeTab,
  onTabChange,
  className,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { isSimpleMode } = useSimpleMode();
  // FIX-CCS-OUTBOX (R3): the pending-count badge on the camera button (the farmer's own waiting + failed photos).
  const outboxBadge = useOutboxBadgeCount();

  const navItems: Array<NavItem & { path: string }> = [
    { id: "home", label: t('nav.home'), icon: Home, path: "/" },
    { id: "capture", label: t('nav.camera'), icon: Camera, path: "/camera", badge: outboxBadge },
    { id: "agent", label: t('agent.name'), icon: MessageCircle, path: "/agent" },
    { id: "score", label: t('nav.score'), path: "/score" },
  ];

  const handleNavigation = (item: typeof navItems[0]) => {
    if ('vibrate' in navigator) {
      navigator.vibrate(15);
    }
    audioFeedback.play('tap');
    navigate(item.path);
    onTabChange(item.id);
  };

  const handlePrefetch = useCallback((path: string) => {
    prefetchRoute(path);
  }, []);

  return (
    <nav
      className={cn(
        "fixed bottom-0 left-0 right-0 z-50",
        "bg-card border-t border-border shadow-lg",
        "pb-[max(env(safe-area-inset-bottom),8px)]",
        className
      )}
    >
      <div className="flex items-center justify-around h-20 px-2">
        {navItems.map((item) => {
          const isActive = location.pathname === item.path;
          const Icon = item.icon;

          return (
            <button
              key={item.id}
              onClick={() => handleNavigation(item)}
              onMouseEnter={() => handlePrefetch(item.path)}
              onFocus={() => handlePrefetch(item.path)}
              onTouchStart={() => handlePrefetch(item.path)}
              className={cn(
                "flex flex-col items-center justify-center relative",
                "min-w-16 min-h-16 w-16 h-16",
                "rounded-xl",
                "transition-all duration-200",
                "active:scale-95 active:bg-muted/50",
                isActive
                  ? "text-primary"
                  : "text-muted-foreground"
              )}
              aria-label={item.label}
              aria-current={isActive ? "page" : undefined}
            >
              <div className="relative flex flex-col items-center gap-1">
                <div className={cn(
                  "relative flex items-center justify-center rounded-full transition-all",
                  isSimpleMode ? cn("w-12 h-12", iconBgColors[item.id]) : ""
                )}>
                  {item.id === "score" ? (
                    <CurrencyIcon size={isSimpleMode ? 44 : 36} />
                  ) : Icon ? (
                    <Icon
                      className={cn("transition-all", isSimpleMode ? "w-9 h-9" : "w-7 h-7")}
                      fill={isActive ? "currentColor" : "none"}
                      strokeWidth={isActive ? 0 : 2}
                    />
                  ) : null}
                  {/* FIX-CCS-POLISH-4 B2: `badge && …` rendered a bare "0" under the camera icon when the
                      outbox was empty (React prints the number 0). Red badge on the icon only when > 0 (S2: hide at 0). */}
                  {(item.badge ?? 0) > 0 && (
                    <Badge
                      size="sm"
                      variant="danger"
                      data-testid={`nav-badge-${item.id}`}
                      className="absolute -top-1.5 -right-2 h-4 min-w-4 px-1 flex items-center justify-center text-[10px]"
                    >
                      {item.badge > 9 ? "9+" : item.badge}
                    </Badge>
                  )}
                </div>
                {isActive && (
                  <div className="w-1.5 h-1.5 rounded-full bg-primary" />
                )}
              </div>
            </button>
          );
        })}
      </div>
    </nav>
  );
};